import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Calendar, MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { setLoading } from 'store/slices/AuthSlice';
import { AuthLayout } from 'layouts/AuthLayout';
import { getData } from 'services/api';
import { ROUTES } from 'features/routes';

import { BodyText, Container, DownloadButton, RightContainer } from './ScannerAccount.styles';
import * as Styled from './ScannerAccount.styles';
import { airbrake } from 'ErrorBoundary';

const iosLink = 'https://apps.apple.com/uy/app/tixologi-producer/id6443567562';
const androidLink = 'https://play.google.com/store/apps/details?id=com.tixologipartner';

interface Event {
	ID?: number;
	id?: number;
	event_id?: number;
	name: string;
	venue: string;
	street_address: string;
	event_days: Array<{
		start_date: string;
		end_date: string;
	}>;
	description: string;
	image: string;
}

const ScannerAccount = () => {
	const { push } = useHistory();

	const [partnerId, setPartnerId] = useState<number | null>(null);

	useEffect(() => {
		const getUserMe = async () => {
			try {
				const response = await getData('/users/me');
				if (response) {
					setPartnerId(response.Partners?.[0]?.ID || null);
				}
			} catch (error) {
				airbrake.notify({
					error,
					context: {
						component: 'ScannerAccount',
						action: 'getUserMe',
					},
				});
			}
		};

		getUserMe();
	}, []);

	useEffect(() => {
		setLoading(false);
	}, []);

	const {
		data: events = [],
		isLoading,
		error,
	} = useQuery({
		queryKey: ['scanner-events', partnerId],
		queryFn: async () => {
			return await getData(`/public/ticketing/events?date=live_or_scheduled&per_page=3&partner_id=${partnerId}`);
		},
		enabled: !!partnerId,
	});

	const navigateToScanner = (eventId: number) => {
		push(ROUTES.SCANNER.replace(':id', eventId.toString()));
	};

	const navigateTo = (platform: string) => () => {
		window.open(platform === 'ios' ? iosLink : androidLink, '_blank');
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	};

	return (
		<AuthLayout>
			<RightContainer>
				<Container>
					<BodyText>
						Select an event to start scanning tickets, or download the mobile app for on-the-go scanning.
					</BodyText>

					{isLoading ? (
						<Styled.LoadingText>Loading events...</Styled.LoadingText>
					) : error ? (
						<Styled.NoEventsText>Error loading events. Please try again.</Styled.NoEventsText>
					) : events.length > 0 ? (
						<Styled.EventsGrid>
							{events.map((event: Event) => {
								const eventId = event.ID || event.id || event.event_id;
								if (!eventId) {
									return null;
								}
								return (
									<Styled.EventCard key={eventId} onClick={() => navigateToScanner(eventId)}>
										{event.image && <Styled.EventImage src={event.image} alt={event.name} />}
										<Styled.EventContent>
											<Styled.EventTitle>{event.name}</Styled.EventTitle>

											<Styled.EventDetails>
												<Styled.DetailItem>
													<MapPin size={16} />
													<Styled.DetailText>{event.venue}</Styled.DetailText>
												</Styled.DetailItem>

												{event.event_days?.[0] && (
													<Styled.DetailItem>
														<Calendar size={16} />
														<Styled.DetailText>{formatDate(event.event_days[0].start_date)}</Styled.DetailText>
													</Styled.DetailItem>
												)}
											</Styled.EventDetails>

											{event.description && (
												<Styled.EventDescription>
													{event.description.length > 100
														? `${event.description.substring(0, 100)}...`
														: event.description}
												</Styled.EventDescription>
											)}
										</Styled.EventContent>
									</Styled.EventCard>
								);
							})}
						</Styled.EventsGrid>
					) : (
						<Styled.NoEventsText>No live or scheduled events found.</Styled.NoEventsText>
					)}

					<Styled.Divider />

					<Styled.MobileSection>
						<Styled.MobileTitle>Mobile Scanner App</Styled.MobileTitle>
						<Styled.MobileText>Download our mobile app for scanning tickets on the go.</Styled.MobileText>

						<Styled.ButtonGroup>
							<DownloadButton text="Download for iOS" category="primary" onClick={navigateTo('ios')} />
							<DownloadButton text="Download for Android" category="primary" onClick={navigateTo('android')} />
						</Styled.ButtonGroup>
					</Styled.MobileSection>
				</Container>
			</RightContainer>
		</AuthLayout>
	);
};

export { ScannerAccount };
